/* eslint-disable react-hooks/set-state-in-effect --
 * v77.99.75: Admin roster section. Reads `data?.admins` and `viewer_is_primary`
 * during render after an effect-driven fetch, and the AddAdminDialog handler
 * calls e.preventDefault() before setState — both flagged by the experimental
 * eslint-plugin-react-hooks v5 rule but intentional and safe.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ShieldCheck, Shield, Lock, Plus, Trash2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatDT } from "@/lib/format";

// Admin roster management. Any admin can view the list (so they can see
// who has access alongside them), but only the *primary* admin can promote
// or revoke. The backend enforces this — the UI just hides the controls
// from non-primary admins to avoid confusing button states.
export const AdminManagementSection = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/admins");
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load admins");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  const viewerIsPrimary = !!data?.viewer_is_primary;
  const admins = data?.admins || [];

  return (
    <section className="space-y-3" data-testid="admin-management-section">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <h2 className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium inline-flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5" /> Admin accounts
        </h2>
        <div className="text-xs text-[#6F828A] font-mono-data tabular-nums">
          {admins.length.toLocaleString()} admin{admins.length === 1 ? "" : "s"}
        </div>
      </div>

      <div className="border border-[#D9E1E3] bg-white rounded-md overflow-hidden" data-testid="admin-management-list">
        {admins.length === 0 && busy ? (
          <div className="p-6 text-center text-sm text-[#6F828A]">Loading…</div>
        ) : (
          <ul className="divide-y divide-[#EEF3F4]">
            {admins.map((a) => (
              <AdminRow
                key={a.email}
                a={a}
                viewerIsPrimary={viewerIsPrimary}
                onRevoke={async () => {
                  if (!window.confirm(`Remove admin access from ${a.email}? They will lose access to the admin panel immediately.`)) return;
                  try {
                    await api.delete(`/admin/admins?email=${encodeURIComponent(a.email)}`);
                    toast.success("Admin access revoked");
                    load();
                  } catch (e) { toast.error(e.response?.data?.detail || "Couldn't revoke"); }
                }}
              />
            ))}
          </ul>
        )}
        <div className="p-3 border-t border-[#EEF3F4] bg-[#F7F9FA] flex flex-wrap items-center justify-between gap-2">
          <div className="text-[11px] text-[#6F828A]">
            Primary admin: <span className="font-mono-data text-[#2E3A3F]">{data?.primary_email || "—"}</span> — cannot be removed.
          </div>
          {viewerIsPrimary ? (
            <Button
              type="button"
              size="sm"
              onClick={() => setAddOpen(true)}
              className="bg-[#1F9FA4] hover:bg-[#198185] text-white rounded-sm"
              data-testid="admin-management-add-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Grant admin access
            </Button>
          ) : (
            <div className="text-[11px] text-[#6F828A] inline-flex items-center gap-1.5">
              <Lock className="w-3 h-3" /> Only the primary admin can add or remove admins.
            </div>
          )}
        </div>
      </div>

      <AddAdminDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onDone={() => { setAddOpen(false); load(); }}
      />
    </section>
  );
};

const SOURCE_LABELS = {
  primary: { label: "Primary", cls: "bg-[#D6F0EF] text-[#116B6E] border-[#A7D8DA]" },
  env:     { label: "Environment", cls: "bg-[#FDF4E2] text-[#8F6B1F] border-[#E6CFA2]" },
  granted: { label: "Granted", cls: "bg-[#ECE3F3] text-[#4E3877] border-[#C5B2DE]" },
};

const AdminRow = ({ a, viewerIsPrimary, onRevoke }) => {
  const src = SOURCE_LABELS[a.source] || SOURCE_LABELS.granted;
  return (
    <li className="p-4 flex items-start gap-4 flex-wrap sm:flex-nowrap" data-testid={`admin-management-row-${a.email}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Shield className="w-3.5 h-3.5 text-[#1F9FA4]" />
          <span className="font-medium text-[#2E3A3F]">{a.name || (a.has_account ? "(unnamed)" : "(no account yet)")}</span>
          <span className="text-xs text-[#6F828A] font-mono-data">{a.email}</span>
          <span className={`text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 rounded-sm border ${src.cls}`}>{src.label}</span>
          {!a.removable && (
            <span className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Locked</span>
          )}
        </div>
        <div className="text-xs text-[#6F828A] mt-1 flex flex-wrap gap-x-3 gap-y-1">
          {a.source === "primary" && (
            <span>Hard-coded — always retains admin access.</span>
          )}
          {a.source === "env" && (
            <span>Configured via <span className="font-mono-data text-[#2E3A3F]">ADMIN_EMAILS</span> env — manage in deploy config.</span>
          )}
          {a.source === "granted" && a.granted_by && (
            <span>Granted by <span className="text-[#2E3A3F] font-mono-data">{a.granted_by}</span></span>
          )}
          {a.source === "granted" && a.granted_at && (
            <span>On <span className="text-[#2E3A3F] font-mono-data">{formatDT(a.granted_at)}</span></span>
          )}
          {!a.has_account && (
            <span className="text-[#8C2A22]">No account registered with this email yet.</span>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
        {viewerIsPrimary && a.removable && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onRevoke}
            className="border-[#E8B8B2] text-[#8C2A22] hover:bg-[#FBEFED] rounded-sm"
            data-testid={`admin-management-revoke-${a.email}`}
          >
            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Remove admin
          </Button>
        )}
      </div>
    </li>
  );
};

const AddAdminDialog = ({ open, onClose, onDone }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setEmail(""); }, [open]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const v = email.trim().toLowerCase();
    if (!v) { toast.error("Enter an email"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/admins", { email: v });
      if (data.already_admin) {
        toast.info(`${v} is already an admin`);
      } else {
        toast.success(`${v} is now an admin`);
      }
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't grant admin");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent preventOutsideClose className="bg-white border-[#D9E1E3] max-w-md" data-testid="admin-management-add-dialog">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle className="font-heading">Grant admin access</DialogTitle>
            <DialogDescription className="text-[#2E3A3F]">
              The account must already exist. They&apos;ll immediately gain access to the admin panel, analytics, feature requests and user access controls.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm mt-2">
            <div>
              <label className="block text-xs uppercase tracking-[0.08em] text-[#6F828A] mb-1">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                data-testid="admin-management-add-email"
                autoFocus
                required
              />
            </div>
            <p className="text-[11px] text-[#6F828A] italic">All admin changes are written to the audit log.</p>
          </div>
          <DialogFooter className="mt-4">
            <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button
              type="submit"
              disabled={busy || !email.trim()}
              className="bg-[#1F9FA4] hover:bg-[#198185] text-white rounded-sm"
              data-testid="admin-management-add-submit"
            >
              {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : "Grant admin access"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
